/**
 * Custom fetcher for SWR that:
 * - Automatically includes authentication token from localStorage
 * - Supports both GET and POST requests
 * - Allows custom headers
 * - Handles errors appropriately
 * - Automatically prepends base URL for relative paths
 */

import { API_BASE_URL } from "./config";

interface FetcherOptions extends RequestInit {
	method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
	body?: any;
	headers?: Record<string, string>;
	requireAuth?: boolean; // Whether to require authentication (default: true)
}

/**
 * Normalizes URL - prepends base URL if URL is relative
 */
function normalizeUrl(url: string): string {
	// If URL is already absolute (starts with http:// or https://), return as is
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	
	// Otherwise, prepend base URL
	return `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export async function fetcher<T = any>(
	url: string,
	options: FetcherOptions = {},
): Promise<T> {
	const {
		method = "GET",
		body,
		headers = {},
		requireAuth = true,
		...restOptions
	} = options;

	// Normalize URL to include base URL if relative
	const normalizedUrl = normalizeUrl(url);

	// Get access token from localStorage if authentication is required
	let accessToken: string | null = null;
	if (requireAuth && typeof window !== "undefined") {
		accessToken = localStorage.getItem("accessToken");
		
		if (!accessToken) {
			throw new Error("Access token not found. Please authenticate first.");
		}
	}

	// Prepare headers
	const defaultHeaders: Record<string, string> = {
		"Content-Type": "application/json",
		Accept: "*/*",
		"Accept-Language": "en-US,en;q=0.9",
		...(requireAuth && accessToken && { Authorization: `Bearer ${accessToken}` }),
		...headers,
	};

	// Prepare request options
	const requestOptions: RequestInit = {
		method,
		headers: defaultHeaders,
		...(body && { body: typeof body === "string" ? body : JSON.stringify(body) }),
		...restOptions,
	};

	// Make the request
	const response = await fetch(normalizedUrl, requestOptions);

	// Handle 401 Unauthorized - clear all storage (redirect handled by SWR config)
	if (response.status === 401 && requireAuth) {
		if (typeof window !== "undefined") {
			// Clear all localStorage
			localStorage.clear();
			// Clear all sessionStorage
			sessionStorage.clear();
		}
		const error = new Error("Unauthorized - Please login again") as Error & {
			status?: number;
		};
		error.status = 401;
		throw error;
	}

	// Handle other errors
	if (!response.ok) {
		const errorText = await response.text();
		let errorMessage = `Request failed with status ${response.status}`;
		
		try {
			const errorData = JSON.parse(errorText);
			errorMessage = errorData.message || errorData.error || errorData.details || errorMessage;
		} catch {
			errorMessage = errorText || errorMessage;
		}

		const error = new Error(errorMessage) as Error & {
			status?: number;
		};
		error.status = response.status;
		throw error;
	}

	// Parse and return response
	const text = await response.text();
	if (!text) {
		return null as T;
	}

	try {
		return JSON.parse(text) as T;
	} catch {
		return text as T;
	}
}

